import qs from 'qs';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { deepTransformKeys } from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { WorkflowFieldObjectKey, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowRedactableFieldConfig } from 'dashboard/models/workflow-schema';
import { dashboardApi } from 'dashboard/redux/api/api';
import { addObjectSchemas, ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import {
  SCHEMA_OBJECTS_EXTRACTED_GROUP_1,
  SCHEMA_OBJECTS_EXTRACTED_GROUP_2,
  SCHEMA_OBJECTS_EXTRACTED_GROUP_3,
} from 'dashboard/redux/thunk-actions/workflow-schema';
import { ApiEndpoint, ApiModel, ApiResponse } from 'lib/interfaces/api';

const getCriteriaVariableSchemasQuery = async () => {
  const responses = await Promise.all([
    SessionClientDashboard.getResourceMetaTyped(ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas, {
      filter: {
        objects: [
          ...SCHEMA_OBJECTS_EXTRACTED_GROUP_1,
          ...SCHEMA_OBJECTS_EXTRACTED_GROUP_2,
          ...SCHEMA_OBJECTS_EXTRACTED_GROUP_3,
        ],
        mode: 'exclude',
      },
    }),
    SessionClientDashboard.getResourceMetaTyped(ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas, {
      filter: {
        objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_1,
        mode: 'include',
      },
    }),
    SessionClientDashboard.getResourceMetaTyped(ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas, {
      filter: {
        objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_2,
        mode: 'include',
      },
    }),
    SessionClientDashboard.getResourceMetaTyped(ApiEndpoint.WorkflowSchemaCriteriaVariableSchemas, {
      filter: {
        objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_3,
        mode: 'include',
      },
    }),
  ]);
  return responses.reduce((combined, { meta }) => {
    const criteriaVariableSchemas = meta?.['criteria-variable-schemas'] || {};
    return {
      ...combined,
      ...criteriaVariableSchemas,
    };
  }, {});
};

const getOperatorsQuery = async () => {
  const { meta } = await SessionClientDashboard.getResourceMetaTyped(
    ApiEndpoint.WorkflowSchemaOperators,
    {
      filter: {},
    },
  );
  return meta?.operators || {};
};

const getWorkflowActionsQuery = async () => {
  const { meta } = await SessionClientDashboard.getResourceMetaTyped(
    ApiEndpoint.WorkflowSchemaWorkflowActions,
    {
      filter: {},
    },
  );
  return meta?.['generic-workflow-actions'] || [];
};

type WorkflowObjectJsonSchemaFilter =
  | {
      objects: (WorkflowFieldObjectKey | ApiModel)[];
      mode?: 'exclude';
    }
  | {
      objects: (WorkflowFieldObjectKey | ApiModel)[];
      mode: 'exactly';
      'template-id': string;
    };

const getWorkflowObjectJsonSchemaQuery = async (filter: WorkflowObjectJsonSchemaFilter) => {
  const { meta } = await SessionClientDashboard.getResourceMeta<ObjectSchemaMap>(
    ApiEndpoint.WorkflowSchemaJsonSchema,
    `?${qs.stringify({ filter })}`,
  );

  return meta?.['json-schema'] || {};
};

const getPrimitivesQuery = async () => {
  const { meta } = await SessionClientDashboard.getResourceMetaTyped(
    ApiEndpoint.WorkflowSchemaPrimitives,
    {
      filter: {},
    },
  );
  return meta?.primitives || {};
};

const getBaseWorkflowObjectJsonSchemasQuery = async (templates: TemplateFilter[]) => {
  const responses = await Promise.all([
    SessionClientDashboard.getResourceMeta<ObjectSchemaMap>(
      ApiEndpoint.WorkflowSchemaJsonSchema,
      `?${qs.stringify({
        filter: {
          objects: [
            ...SCHEMA_OBJECTS_EXTRACTED_GROUP_1,
            ...SCHEMA_OBJECTS_EXTRACTED_GROUP_2,
            ...SCHEMA_OBJECTS_EXTRACTED_GROUP_3,
          ],
          mode: 'exclude',
        },
      })}`,
    ),
    SessionClientDashboard.getResourceMeta<ObjectSchemaMap>(
      ApiEndpoint.WorkflowSchemaJsonSchema,
      `?${qs.stringify({
        filter: {
          objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_1,
        },
      })}`,
    ),
    SessionClientDashboard.getResourceMeta<ObjectSchemaMap>(
      ApiEndpoint.WorkflowSchemaJsonSchema,
      `?${qs.stringify({
        filter: {
          objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_2,
        },
      })}`,
    ),
    SessionClientDashboard.getResourceMeta<ObjectSchemaMap>(
      ApiEndpoint.WorkflowSchemaJsonSchema,
      `?${qs.stringify({
        filter: {
          objects: SCHEMA_OBJECTS_EXTRACTED_GROUP_3,
        },
      })}`,
    ),
    ...templates.map(({ type, id }) =>
      SessionClientDashboard.getResourceMeta<ObjectSchemaMap>(
        ApiEndpoint.WorkflowSchemaJsonSchema,
        `?${qs.stringify({
          filter: {
            mode: 'exactly',
            objects: [type],
            'template-id': id,
          },
        })}`,
      ),
    ),
  ]);
  return responses.reduce((combined, { meta }) => {
    const objectSchema = meta?.['json-schema'] || {};
    return {
      ...combined,
      ...objectSchema,
    };
  }, {});
};

export const workflowSchemaApi = dashboardApi.injectEndpoints({
  endpoints: (builder) => ({
    getCriteriaVariableSchemas: builder.query<WorkflowSchema['criteria-variable-schemas'], void>({
      query: getCriteriaVariableSchemasQuery,
    }),
    getOperators: builder.query<WorkflowSchema['operators'], void>({
      query: getOperatorsQuery,
    }),
    getWorkflowActions: builder.query<WorkflowSchema['generic-workflow-actions'], void>({
      query: getWorkflowActionsQuery,
    }),
    getWorkflowObjectJsonSchema: builder.query<ObjectSchemaMap, WorkflowObjectJsonSchemaFilter>({
      query: getWorkflowObjectJsonSchemaQuery,
      transformResponse: (response: ApiResponse<never, ObjectSchemaMap>) =>
        deepTransformKeys(response),
    }),
    getBaseWorkflowObjectJsonSchemas: builder.query<ObjectSchemaMap, TemplateFilter[]>({
      query: getBaseWorkflowObjectJsonSchemasQuery,
      transformResponse: (response: ApiResponse<never, ObjectSchemaMap>) =>
        deepTransformKeys(response),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(addObjectSchemas(data));
      },
    }),
    getWorkflowSchema: builder.query<
      WorkflowSchema,
      { loadCriteriaVariableSchemas: boolean; loadPrimitives: boolean }
    >({
      query: async ({ loadCriteriaVariableSchemas, loadPrimitives }) => {
        // TODO @jkim - change this to a thunk that calls each endpoint so we can leverage caching at each level
        const [cvsResponse, operatorsResponse, actionsResponse, primitivesResponse] =
          await Promise.all([
            loadCriteriaVariableSchemas ? getCriteriaVariableSchemasQuery() : Promise.resolve({}),
            getOperatorsQuery(),
            getWorkflowActionsQuery(),
            loadPrimitives ? getPrimitivesQuery() : Promise.resolve({}),
          ]);

        return {
          'criteria-variable-schemas': cvsResponse,
          operators: operatorsResponse,
          'generic-workflow-actions': actionsResponse,
          primitives: deepTransformKeys(primitivesResponse),
        };
      },
    }),
    getRedactableFieldConfigs: builder.query<WorkflowRedactableFieldConfig[], void>({
      query: async () => {
        const { meta } = await SessionClientDashboard.getResourceMetaTyped(
          ApiEndpoint.WorkflowSchemaRedactableFieldConfigs,
          {
            filter: {},
          },
        );

        return meta?.['redactable-field-configs'] || [];
      },
    }),
  }),
});

// Hooks are autogenerated based on the defined endpoints
export const {
  useGetCriteriaVariableSchemasQuery,
  useGetOperatorsQuery,
  useGetWorkflowActionsQuery,
  useGetWorkflowSchemaQuery,
  useGetWorkflowObjectJsonSchemaQuery,
  useGetBaseWorkflowObjectJsonSchemasQuery,
  useGetRedactableFieldConfigsQuery,
  useLazyGetWorkflowObjectJsonSchemaQuery,
  endpoints: workflowSchemaEndpoints,
} = workflowSchemaApi;
